import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractErrorMessage, useAdminPatch } from '@/queries/ticketMutations';
import { useCompanySearch, useUserSearch } from '@/queries/people';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SearchResults } from './SearchResults';
import type { CompanySearchResult, Ticket, TicketRequester, UserSearchResult } from '@/types/ticket';

// A picked value, shown as a chip with a clear button in place of its search box.
function Picked({ label, children, onClear }: { label: string; children: ReactNode; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-etn-menu border border-etn-border-input bg-etn-surface px-3.5 py-2 text-etn-control">
      <div className="min-w-0">{children}</div>
      <Button type="button" variant="ghost" size="icon-xs" aria-label={`Clear ${label}`} onClick={onClear}>
        <X />
      </Button>
    </div>
  );
}

function UserRow({ user }: { user: UserSearchResult }) {
  return (
    <>
      <div className="truncate font-semibold text-etn-text-cell">{user.name || user.email || user._id}</div>
      <div className="truncate text-etn-micro text-etn-text-soft">{[user.email, user.companyName].filter(Boolean).join(', ')}</div>
    </>
  );
}

export function ChangeRequesterDialog({ ticket, requester, trigger }: { ticket: Ticket; requester: TicketRequester; trigger: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [pickedUser, setPickedUser] = useState<UserSearchResult | null>(null);
  // null means "leave the company as it is".
  const [pickedCompany, setPickedCompany] = useState<CompanySearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const patch = useAdminPatch(ticket._id);

  const debouncedUser = useDebouncedValue(userQuery);
  const debouncedCompany = useDebouncedValue(companyQuery);
  const users = useUserSearch(open && !pickedUser ? debouncedUser : '');
  const companies = useCompanySearch(open ? debouncedCompany : '');

  const reset = () => {
    setUserQuery('');
    setCompanyQuery('');
    setPickedUser(null);
    setPickedCompany(null);
    setError(null);
  };

  const company = pickedCompany ?? { _id: ticket.companyId, name: requester.companyName ?? undefined };
  const body: { requesterId?: string; companyId?: string } = {};
  if (pickedUser && pickedUser._id !== ticket.createdBy) body.requesterId = pickedUser._id;
  if (company._id !== ticket.companyId) body.companyId = company._id;
  const changed = Object.keys(body).length > 0;

  const pickUser = (user: UserSearchResult) => {
    setPickedUser(user);
    setUserQuery('');
    setError(null);
    // The ticket follows the requester unless staff then pick a company themselves.
    if (user.companyId) setPickedCompany({ _id: user.companyId, name: user.companyName });
  };

  const save = async () => {
    if (!changed) return;
    setError(null);
    try {
      await patch.mutateAsync(body);
      toast.success('Requester updated');
      setOpen(false);
      reset();
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not change the requester'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change requester</DialogTitle>
          <DialogDescription>
            Use this when a ticket was filed under the wrong person, for example a forwarded email. The ticket moves to the requester&apos;s company
            and gets a new reference.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="change-requester-user">Requester</Label>
            {pickedUser ? (
              <Picked
                label="requester"
                onClear={() => {
                  setPickedUser(null);
                  setPickedCompany(null);
                }}
              >
                <UserRow user={pickedUser} />
              </Picked>
            ) : (
              <>
                <Input
                  id="change-requester-user"
                  autoComplete="off"
                  placeholder={requester.name || requester.email || 'Search by name or email'}
                  value={userQuery}
                  onChange={e => setUserQuery(e.target.value)}
                />
                <SearchResults
                  label="Matching users"
                  term={userQuery}
                  items={users.data}
                  isFetching={users.isFetching || userQuery !== debouncedUser}
                  onPick={pickUser}
                  renderItem={user => <UserRow user={user} />}
                />
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-requester-company">Company</Label>
            <Input
              id="change-requester-company"
              autoComplete="off"
              placeholder={company.name || 'Search companies'}
              value={companyQuery}
              onChange={e => setCompanyQuery(e.target.value)}
            />
            <SearchResults
              label="Matching companies"
              term={companyQuery}
              items={companies.data}
              isFetching={companies.isFetching || companyQuery !== debouncedCompany}
              onPick={c => {
                setPickedCompany(c);
                setCompanyQuery('');
                setError(null);
              }}
              renderItem={c => <span className="text-etn-text-cell">{c.name || c._id}</span>}
            />
            <p className="text-etn-micro text-etn-text-soft" data-testid="change-requester-company">
              Moves to: <span className="font-semibold text-etn-text-cell">{company.name || company._id}</span>
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-etn-danger-text">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" disabled={!changed || patch.isPending} onClick={save}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
